<!-- Main Fahrdienst 452 Module view with document viewer and "Perlschnur" display -->
<template>
  <responsive-drawer-layout :isContentPdf="displayedDocumentUrl !== null" contentUrl="pdfJsDocumentUrl">
    <template v-slot:drawer>
      <fahrdienst-docs-by-doc-type
        :v-model="line || track || tourHash || dateIsoString"
        v-if="isLoaded"
        :showFilter="true"
        :showSorting="true"
        :storeLabelSelection="true"
        :storeCategorySelection="true"
        :categories="fahrdienst452FileTypes"
        :selectedDocument="displayedDocument"
        :filteredDocuments="relatedDocs"
        :labels="fahrdienstLabels"
        :filteredDisplayText="relevantDocsText"
        :totalUnreadCount="unreadCount"
        categoryPropertyName="typeIdFk"
        @docSelected="onDocumentSelected"
        :updateRequired="updateRequired"
      >
      </fahrdienst-docs-by-doc-type>
    </template>

    <template v-slot:content>
      <!-- Document display -->
      <div v-if="shouldDisplayDocument" class="mx-0 fahrdienst452PdfContainer inherit-height">
        <pdf-js :url="displayedDocumentUrl" :download-file-name="downloadFileName"></pdf-js>
      </div>

      <!-- loading display -->
      <info-card v-if="!isLoaded" :textRows="[$t('isLoading.moduleIsGettingLoaded')]">
        <template v-slot:title>{{ $t('isLoading.title') }}</template>
      </info-card>

      <div class="fill-container" id="fahrdienst452Container">
        <div v-if="shouldDisplayTour" class="px-1 px-sm-2 inherit-height fill-container-inner">
          <perlschnur
            v-if="isLoaded"
            ref="perlschnur"
            caller="fahrdienst452"
            :tenant="tenant"
            :perlschnurHeader="moduleConfig.perlschnurHeader"
            :perlschnurColumns="moduleConfig.perlschnurColumns"
            :perlschnurColumnsXs="moduleConfig.perlschnurColumnsXs"
            :scrollAreaPixelHeight="mx_contentHeight - 159"
            :startToMinutesAfterMidnight="startToMinutesAfterMidnight"
            :endToMinutesAfterMidnight="endToMinutesAfterMidnight"
            :line="line"
            :track="track"
            :tourHashParam="tourHash"
            :dateIsoString="dateIsoString"
            :dateString="dateYYYYMMDD"
            :initiallyScrollToCurrentTime="isCurrentDate"
            :autoScrollToCurrentTime="autoScrollToCurrentTime && isCurrentDate"
          />
        </div>

        <info-card v-else>
          <template v-if="useUmlaufLinieKursSelektion" v-slot:title>{{ $t('fahrdienst452.noSelectedUmlauf.title') }}</template>
          <template v-else v-slot:title>{{ $t('fahrdienst452.noSelectedTrack.title') }}</template>
          <template v-slot:content>
            <div v-if="useUmlaufLinieKursSelektion">{{ $t('fahrdienst452.noSelectedUmlauf') }}</div>
            <div v-else>{{ $t('fahrdienst452.noSelectedTrack') }}</div>
          </template>
        </info-card>
      </div>
    </template>
  </responsive-drawer-layout>
</template>

<script>
import { mapMutations, mapState, mapGetters } from 'vuex';
import common from '@/mixins/common';
import configMixin from '@/mixins/configMixin';
import dateTimeMixin from '@/mixins/dateTimeMixin';
import FahrdienstDocsByDocType from '@/components/fahrdienstDocs/FahrdienstDocsByDocType.vue';
import ResponsiveDrawerLayout from '@/layouts/ResponsiveDrawerLayout';
import dateUtils from '@/common/dateUtils';
import PdfJs from '@/components/pdf/PdfJs';
import fahrdienst452Service from './fahrdienst452Service';
import nonReactiveStore from './storeNonReactive';
import Perlschnur from '@/modules/common/Perlschnur.vue';
import InfoCard from '@/components/container/InfoCard';
import index from './index';
import uiHelpers from '@/common/uiHelpers';
import {
  ROUTE_FAHRDIENST,
  ROUTE_FAHRDIENST_TOUR_FILE,
  ROUTE_FAHRDIENST_TOUR_DATE_LINE_TRACK_FILE,
  ROUTE_FAHRDIENST_TOUR_DATE_FILE,
  ROUTE_FAHRDIENST_TOUR_LINE_TRACK_FILE,
  ROUTE_FAHRDIENST_HASH_FILE,
  ROUTE_FAHRDIENST_HASH_DATE_TOUR_FILE,
  ROUTE_FAHRDIENST_HASH_DATE_FILE,
  ROUTE_FAHRDIENST_HASH_TOUR_FILE,
} from './routes';
import fahrdienst452Mixin from '@/modules/fahrdienst452/fahrdienst452Mixin';
import readService from '../../services/readService';

const ROOT = { root: true };

export default {
  name: 'Fahrdienst452',
  mixins: [common, configMixin, dateTimeMixin, fahrdienst452Mixin],
  components: {
    ResponsiveDrawerLayout,
    FahrdienstDocsByDocType,
    PdfJs,
    InfoCard,
    Perlschnur,
  },
  data() {
    return {
      endToMinutesAfterMidnight: null,
      startToMinutesAfterMidnight: null,
      renderingStarted: false,
      relatedDocsOnly: false,
      updateRequired: false, // helper property used to indicate the FahrdienstDocsByDocType that an update is required, namely when a document has
      // been marked as read/unread. This fact changes the sort of the documents which cannot be detected by the subcomponent
    };
  },
  computed: {
    ...mapState({ tenant: (state) => state.profile.customerRelations.tenant }),
    ...mapState('fahrdienst452', [
      'isLoaded',
      'fahrdienst452FileTypes',
      'fahrdienst452File',
      'fahrdienst452FileById',
      'fahrdienstLabels',
      'autoScrollToCurrentTime',
      'displayMode',
      'displayedDocument',
      'unreadCount',
    ]),
    ...mapGetters('fahrdienst452', ['getIsLoaded']),
    shouldDisplayDocument() {
      return this.isLoaded && this.isDocumentRoute && (!this.displayHint || this.displayHint === 'doc');
    },
    downloadFileName() {
      const selectedDoc = this.fahrdienst452FileById[this.$route.params.documentId];
      if (!selectedDoc) return '';

      const { filename, title } = selectedDoc;

      const extension = filename.split('.')[1];

      if (!extension) return '';

      return `${title}.${extension}`;
    },
    shouldDisplayTour() {
      return this.isLoaded && (this.routeHasTour || this.routeHasHash) && !this.displayHint !== 'doc';
    },
    filterField() {
      return this.moduleConfig.filterField ? this.moduleConfig.filterField : 'kurs';
    },
    fahrdienst452FileFilteringColumns() {
      return this.moduleConfig.fahrdienst452FileFilteringColumns ? this.moduleConfig.fahrdienst452FileFilteringColumns : ['kurs', 'linie'];
    },
    displayedDocument() {
      if (this.isLoaded && this.$route.params.documentId) {
        return this.fahrdienst452FileById[this.$route.params.documentId];
      }
      return null;
    },
    dateIsoString() {
      if (this.dateYYYYMMDD) {
        return dateUtils.reformatYYYYMMDDAsDateIsoString(this.dateYYYYMMDD);
      }
      return dateUtils.reformatYYYYMMDDAsDateIsoString(dateUtils.formatDateAsYYYYMMDD(dateUtils.parseISOString(new Date().toString())));
    },
    isCurrentDate() {
      return this.dateIsoString ? dateUtils.isSameDateIsoString(this.dateIsoString, new Date().toISOString()) : false;
    },
    displayedDocumentUrl() {
      return this.constructPdfJsFileUrl(index.api, this.displayedDocument, 'filename');
    },
    relevantDocsText() {
      return this.$t('fahrdienst.displayRelatedDocsOnly');
    },
    /* If a track is selected, the related docs get returned */
    relatedDocs() {
      let selectedDate = this.$route.params.dateYYYYMMDD ? dateUtils.parseYYYYMMDD(this.$route.params.dateYYYYMMDD) : null;

      let date = selectedDate || new Date();
      let lines = this.line ? [this.line] : [];
      let tracks = this.track ? [this.track] : [];

      console.debug(`calculate relatedDocs for tracks=${tracks} this.tourHash=${this.tourHash}`);
      // In case of the perlschnur specified by the tourHash, retrieve lines and tracks from the tour
      if (this.tourHash) {
        let tour = nonReactiveStore.timetableByTourHash[this.tourHash];
        lines = tour.linien ? tour.linien.split(',') : [];
        tracks = tour.kurs ? [tour.kurs] : [];
      }
      console.debug(`relatedDocs for lines=${lines}, kurs=${tracks}`);

      let doFilterByLineOrTrack = true;
      if (doFilterByLineOrTrack && lines.length == 0 && tracks.length == 0) {
        doFilterByLineOrTrack = false;
      }

      return fahrdienst452Service.filterDocuments({
        fahrdienst452Files: this.fahrdienst452File,
        filterByLineOrTrack: doFilterByLineOrTrack,
        lines,
        tracks,
        selectedDate: date,
        fahrdienst452FileFilteringColumns: this.fahrdienst452FileFilteringColumns,
      });
    },
  },
  methods: {
    ...mapMutations('fahrdienst452', ['setSelectedDateLineTrack', 'setDisplayedDocument', 'displayDocument']),
    init() {
      if (!this.isLoaded) return;

      let { dateYYYYMMDD, line, track, documentId, tourHash } = this.$route.params;
      console.debug('init with  dateYYYYMMDD, line, track,documentId', dateYYYYMMDD, line, track, documentId);

      if ((this.isTourRoute || this.isHashRoute) && dateYYYYMMDD && ((line && track) || tourHash) && (!this.displayHint || this.displayHint == 'tour')) {
        console.debug('set display mode "tour"');
        this.$store.commit('fahrdienst452/setDisplayMode', 'tour', ROOT);
      }
      let document = this.displayedDocument;
      if (documentId && document) {
        console.debug('set display mode "document"');
        this.$store.commit('fahrdienst452/setDisplayedDocument', document, ROOT);
        this.$store.commit('fahrdienst452/setDisplayMode', 'document', ROOT);

        uiHelpers.scrollToFileOfFileList(document, 1000);
        if (readService.isUnread(document)) {
          uiHelpers.initiateDelayedMarkAsRead(this.$store.dispatch, document, 1500);
        }
      }
      if (this.routeName === ROUTE_FAHRDIENST) {
        this.$store.commit('fahrdienst452/resetView', {}, ROOT);
      }
    },
    onDocumentSelected(document) {
      // use != instead of !== because the route param is a string whereas document.id is a Number
      if (Number(this.$route.params.documentId) !== document.id) {
        let { dateYYYYMMDD, line, track, tourHash } = this.$route.params;

        if (this.useLinieKursSelektion) {
          if (dateYYYYMMDD && line && track) {
            this.$router.push({
              name: ROUTE_FAHRDIENST_TOUR_DATE_LINE_TRACK_FILE,
              params: { dateYYYYMMDD, line, track, documentId: document.id },
              query: { display: 'doc' },
            });
          } else if (dateYYYYMMDD && !line && !track) {
            this.$router.push({ name: ROUTE_FAHRDIENST_TOUR_DATE_FILE, params: { dateYYYYMMDD, documentId: document.id } });
          } else if (!dateYYYYMMDD && line && track) {
            this.$router.push({ name: ROUTE_FAHRDIENST_TOUR_LINE_TRACK_FILE, params: { line, track, documentId: document.id }, query: { display: 'doc' } });
          } else {
            this.$router.push({ name: ROUTE_FAHRDIENST_TOUR_FILE, params: { documentId: document.id } });
          }
        }
        if (this.useUmlaufLinieKursSelektion) {
          if (dateYYYYMMDD && tourHash) {
            this.$router.push({
              name: ROUTE_FAHRDIENST_HASH_DATE_TOUR_FILE,
              params: { dateYYYYMMDD, tourHash, documentId: document.id },
              query: { display: 'doc' },
            });
          } else if (dateYYYYMMDD && !tourHash) {
            this.$router.push({ name: ROUTE_FAHRDIENST_HASH_DATE_FILE, params: { dateYYYYMMDD, documentId: document.id } });
          } else if (!dateYYYYMMDD && tourHash) {
            this.$router.push({ name: ROUTE_FAHRDIENST_HASH_TOUR_FILE, params: { tourHash, documentId: document.id }, query: { display: 'doc' } });
          } else {
            this.$router.push({ name: ROUTE_FAHRDIENST_HASH_FILE, params: { documentId: document.id } });
          }
        }

        if (readService.isUnread(document)) {
          uiHelpers.initiateDelayedMarkAsRead(this.$store.dispatch, document, 1500);
        }

        if (this.mdAndDown) {
          this.mx_isLeftDrawerOpen = false;
        }
      }
    },
  },
  watch: {
    getIsLoaded(newVal) {
      this.init();
    },
    autoScrollToCurrentTime(newValue) {
      if (this.$refs.perlschnur) {
        if (newValue === true) {
          this.$refs.perlschnur.startAutoScrollingToCurrentTime();
        } else {
          this.$refs.perlschnur.stopAutoScrollingToCurrentTime();
        }
      }
    },
    $props: {
      handler(newValue, oldValue) {
        console.debug('Fahrdienst452 property handler', newValue, oldValue);
        this.init();
      },
      deep: true,
      immediate: true,
    },
  },
};
</script>
